import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import { selectAlarmDefinitions } from '@sophocles/features/reference';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import { formatDateTime, formatTime } from '@sophocles/utils/formatUtils';

/**
 * AlarmScheduleCard component displays alarms that occurred during schedule windows
 */
const AlarmScheduleCard = ({ alarmData, user = null }) => {
  const { timestamp, summary, alarm, schedule_count, active_schedules } = alarmData;
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const alarmDefinitions = useSelector(selectAlarmDefinitions);

  const alarmDetails = useMemo(() => {
    if (!alarm?.key) return null;
    return alarmDefinitions.find(a => a.key === alarm.key);
  }, [alarm?.key, alarmDefinitions]);

  const alarmName = useMemo(() => {
    if (alarmDetails) {
      return tAlarm(`alarms.${alarm.key}`, { defaultValue: alarmDetails.display_name ?? alarm.key });
    }
    return alarm?.key || 'Unknown Alarm';
  }, [alarmDetails, alarm?.key, tAlarm]);

  const getSeverityFamily = severity => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'bad';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'neutral';
    }
  };

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <Typography variant="body2" flexGrow={1}>
            <strong>{formatDateTime(timestamp, user)}</strong>
          </Typography>
          <StatusChip label={alarm?.severity || 'Unknown'} color={getSeverityFamily(alarm?.severity)} />
          <Chip
            label={t('operationalReports.scheduleCount', {
              count: schedule_count,
              defaultValue: `${schedule_count} schedule${schedule_count !== 1 ? 's' : ''}`,
            })}
            size="small"
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
            {alarmName}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {summary}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            {t('operationalReports.activeSchedules', {
              count: schedule_count,
              defaultValue: `Active Schedules (${schedule_count}):`,
            })}
          </Typography>

          {active_schedules && active_schedules.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('general.relay', { defaultValue: 'Relay' })}</TableCell>
                  <TableCell>{t('general.schedule', { defaultValue: 'Schedule' })}</TableCell>
                  <TableCell>{t('operationalReports.onTime', { defaultValue: 'On Time' })}</TableCell>
                  <TableCell>{t('operationalReports.offTime', { defaultValue: 'Off Time' })}</TableCell>
                  <TableCell>{t('operationalReports.days', { defaultValue: 'Days' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {active_schedules.map((schedule, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2">
                        Relay #{schedule.relay_number}
                        {schedule.relay_name && (
                          <>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {schedule.relay_name}
                            </Typography>
                          </>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">Schedule #{schedule.schedule_number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {schedule.on_type}
                        {schedule.on_time && ` ${formatTime(schedule.on_time, user)}`}
                        {schedule.on_offset && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {t('operationalReports.offset', {
                              minutes: schedule.on_offset,
                              defaultValue: `Offset: ${schedule.on_offset} min`,
                            })}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {schedule.off_type}
                        {schedule.off_time && ` ${formatTime(schedule.off_time, user)}`}
                        {schedule.off_offset && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {t('operationalReports.offset', {
                              minutes: schedule.off_offset,
                              defaultValue: `Offset: ${schedule.off_offset} min`,
                            })}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{schedule.days_of_week?.join(', ') || 'All'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('operationalReports.noScheduleDetails', { defaultValue: 'No schedule details available' })}
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

AlarmScheduleCard.propTypes = {
  alarmData: PropTypes.shape({
    timestamp: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    alarm: PropTypes.shape({
      id: PropTypes.number,
      key: PropTypes.string,
      severity: PropTypes.string,
      state: PropTypes.bool,
    }),
    schedule_count: PropTypes.number,
    active_schedules: PropTypes.arrayOf(
      PropTypes.shape({
        relay_id: PropTypes.number,
        relay_number: PropTypes.number,
        relay_name: PropTypes.string,
        schedule_id: PropTypes.number,
        schedule_number: PropTypes.number,
        on_type: PropTypes.string,
        on_time: PropTypes.string,
        on_offset: PropTypes.number,
        off_type: PropTypes.string,
        off_time: PropTypes.string,
        off_offset: PropTypes.number,
        days_of_week: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }).isRequired,
  user: PropTypes.object,
};

export default AlarmScheduleCard;
